'use client';

import React from 'react';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import {
  useGetCodeSetsVolumeQuery,
  useGetProviderVolumeQuery,
  useGetReferralVolumeTrendsQuery,
} from 'src/api/redux';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import {
  applyDisplayMode,
  applyCaptureRate,
} from 'src/components/Profiles/shared/widgets/utils';
import MonthlyAvgBadge from 'src/components/Profiles/shared/widgets/MonthlyAvgBadge';
import CaptureRateBadge from 'src/components/Profiles/shared/widgets/CaptureRateBadge';

const CaptureRate: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;
  const { providerId: entityId } = getEntityIdentifiers(entityDetails);

  const volumeSource = widget?.volume_source ?? 'treating';
  const volumeType = widget?.volume_type;
  const captureRate = widget?.capture_rate;
  const displayMode = widget?.display_mode;
  const isMonthlyAvg = displayMode === 'monthly_average';

  const codeSetId = widget?.code_set?.id;
  const encounterId = widget?.encounter?.id;

  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const dateRange =
    minYear != null || maxYear != null
      ? { gte: String(minYear), lte: String(maxYear) }
      : undefined;

  // Treating volume with code_set
  const isTreatingCodeSet =
    volumeSource === 'treating' && !!codeSetId && !encounterId;
  const { data: codeSetsData, isLoading: codeSetsLoading } =
    useGetCodeSetsVolumeQuery(
      {
        entityId: entityId || '',
        params: {
          code_set_ids: codeSetId ? [codeSetId] : [],
          all_code_sets: false,
          ...(minYear != null && { min_year: minYear }),
          ...(maxYear != null && { max_year: maxYear }),
        },
      },
      { skip: !entityId || !isTreatingCodeSet }
    );

  // Treating volume with encounter
  const isTreatingEncounter =
    volumeSource === 'treating' && !!encounterId && !codeSetId;
  const { data: encounterData, isLoading: encounterLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        cohort_id: encounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !isTreatingEncounter }
    );

  // Referral volume (inbound or outbound)
  const isReferral =
    volumeSource === 'inbound_referrals' ||
    volumeSource === 'outbound_referrals';
  const referralDirection =
    volumeSource === 'outbound_referrals' ? 'outbound' : 'inbound';
  const { data: referralData, isLoading: referralLoading } =
    useGetReferralVolumeTrendsQuery(
      {
        entityId: entityId || '',
        codeSetId: codeSetId || 0,
        direction: referralDirection,
        dateRange,
      },
      { skip: !entityId || !isReferral || !codeSetId }
    );

  // Normalize data into { claims, patients }
  const isQueryLoading = isTreatingCodeSet
    ? codeSetsLoading
    : isTreatingEncounter
      ? encounterLoading
      : referralLoading;
  const isLoading = isQueryLoading || !!widgetConfig?.isLoading;

  const normalizedData = isTreatingCodeSet
    ? codeSetsData
      ? {
          claims: Number(codeSetsData.total_claims),
          patients: Number(codeSetsData.total_patients),
        }
      : null
    : isTreatingEncounter
      ? encounterData
        ? {
            claims: Number(encounterData.claims),
            patients: Number(encounterData.patients),
          }
        : null
      : referralData
        ? {
            claims: Number(referralData.claims),
            patients: Number(referralData.patients),
          }
        : null;

  const hasData = !!normalizedData;

  const showPatientsAsHero = volumeType === 'patients';
  const heroLabel = showPatientsAsHero ? 'Patients' : 'Claims';

  const rawHeroValue = hasData
    ? showPatientsAsHero
      ? normalizedData.patients
      : normalizedData.claims
    : 0;

  const displayModeValue = applyDisplayMode(rawHeroValue, displayMode);
  const captureAdjusted = applyCaptureRate(displayModeValue, captureRate);
  const formattedHero = handleNumericValue(captureAdjusted);
  const formattedRaw = handleNumericValue(displayModeValue);

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="capture-rate-widget"
      role="region"
      aria-label={widget?.label ?? 'Capture Rate'}
    >
      <WidgetHeader
        fallbackTitle="Capture Rate"
        trailing={
          <div
            className="flex items-center gap-1.5"
            data-testid="capture-rate-trailing"
          >
            {isMonthlyAvg && <MonthlyAvgBadge />}
            {captureRate != null && <CaptureRateBadge rate={captureRate} />}
          </div>
        }
      />
      <div>
        {isLoading ? (
          <WidgetLoading data-testid="capture-rate-loading" />
        ) : !hasData ? (
          <WidgetEmpty data-testid="capture-rate-empty" variant="full" message="No volume data available" />
        ) : (
          <div
            className="flex flex-col gap-1"
            data-testid="capture-rate-content"
          >
            <div className="flex items-baseline gap-3">
              <span
                className="text-4xl font-bold tabular-nums text-sky-600"
                data-testid="capture-rate-hero-value"
                aria-label={`Capture-adjusted ${heroLabel}: ${formattedHero}`}
              >
                {formattedHero}
              </span>
              <span
                className="text-sm font-semibold text-neutral-500"
                data-testid="capture-rate-hero-label"
              >
                {heroLabel}
              </span>
            </div>
            <p
              className="text-sm text-neutral-500"
              data-testid="capture-rate-raw-total"
            >
              of {formattedRaw} {heroLabel} total
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default CaptureRate;
